import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import {
  getLegalDocumentAdmin,
  saveLegalDocumentDraft,
  publishLegalDocument,
  listLegalDocumentVersions,
  getLegalDocumentVersion,
  restoreLegalDocumentVersion,
} from "@/lib/api/legal.functions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { FormActions } from "@/components/FormActions";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { legalDocumentLabels, legalDocumentPaths, type LegalDocumentSlug } from "@/lib/legal/defaults";
import { addHeadingIds } from "@/lib/legal/parse-headings";

const slugParams = z.object({ slug: z.enum(["privacy", "terms"]) });

export const Route = createFileRoute("/admin/legal/$slug")({
  loader: async ({ params }) => {
    const parsed = slugParams.safeParse(params);
    if (!parsed.success) throw notFound();
    const slug = parsed.data.slug;
    const [document, versions] = await Promise.all([
      getLegalDocumentAdmin({ data: { slug } }),
      listLegalDocumentVersions({ data: { slug } }),
    ]);
    return { slug, document, versions };
  },
  component: EditLegalDocumentPage,
});

function EditLegalDocumentPage() {
  const { slug, document, versions } = Route.useLoaderData();
  const router = useRouter();

  const [title, setTitle] = useState(document.title);
  const [metaTitle, setMetaTitle] = useState(document.metaTitle);
  const [metaDescription, setMetaDescription] = useState(document.metaDescription);
  const [contentHtml, setContentHtml] = useState(document.draftContentHtml);
  const [changeNote, setChangeNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const label = legalDocumentLabels[slug as LegalDocumentSlug];
  const publicPath = legalDocumentPaths[slug as LegalDocumentSlug];

  async function handleSaveDraft() {
    setSaving(true);
    try {
      await saveLegalDocumentDraft({
        data: { slug, title, draftContentHtml: contentHtml, metaTitle, metaDescription },
      });
      toast.success("Draft saved");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save draft");
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      const result = await publishLegalDocument({
        data: {
          slug,
          title,
          draftContentHtml: contentHtml,
          metaTitle,
          metaDescription,
          changeNote: changeNote.trim() || undefined,
        },
      });
      toast.success(`Published as version ${result.version}`);
      setChangeNote("");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to publish");
    } finally {
      setPublishing(false);
    }
  }

  async function handlePreviewVersion(version: number) {
    try {
      const row = await getLegalDocumentVersion({ data: { slug, version } });
      if (!row) {
        toast.error("Version not found");
        return;
      }
      setPreviewVersion(version);
      setPreviewHtml(row.contentHtml);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load version");
    }
  }

  async function handleRestore(version: number) {
    try {
      await restoreLegalDocumentVersion({ data: { slug, version } });
      const row = await getLegalDocumentVersion({ data: { slug, version } });
      if (row) {
        setTitle(row.title);
        setMetaTitle(row.metaTitle ?? document.metaTitle);
        setMetaDescription(row.metaDescription ?? document.metaDescription);
        setContentHtml(row.contentHtml);
      }
      toast.success(`Version ${version} restored to draft — publish to go live`);
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to restore version");
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        title={label}
        action={
          <Button variant="outline" asChild>
            <a href={publicPath} target="_blank" rel="noopener noreferrer">
              View live
            </a>
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Link to="/admin/legal" className="hover:text-foreground">
          Legal documents
        </Link>
        <span>/</span>
        <span>{label}</span>
        {document.currentVersion ? (
          <span className="rounded bg-muted px-2 py-0.5 text-xs">Live: v{document.currentVersion}</span>
        ) : (
          <span className="rounded bg-muted px-2 py-0.5 text-xs">Not yet published from CMS</span>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Page title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <p className="text-xs text-muted-foreground">
                  Use heading 2 for section titles — they appear in the on-page table of contents.
                </p>
                <RichTextEditor value={contentHtml} onChange={setContentHtml} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">SEO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Meta title</Label>
                <Input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Meta description</Label>
                <Textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} rows={3} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Publish</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Change note (optional)</Label>
                <Textarea
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  rows={2}
                  placeholder="e.g. Updated PDPA contact details"
                />
              </div>
              <FormActions className="flex-col sm:flex-row sm:items-stretch">
                <Button type="button" variant="outline" onClick={() => void handleSaveDraft()} disabled={saving}>
                  {saving ? "Saving…" : "Save draft"}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" disabled={publishing}>
                      Publish new version
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Publish {label}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This updates the live page at {publicPath} and stores a new version in history.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => void handlePublish()}>
                        {publishing ? "Publishing…" : "Publish"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </FormActions>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Version history</CardTitle>
            </CardHeader>
            <CardContent>
              {versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No published versions yet.</p>
              ) : (
                <ul className="space-y-3">
                  {versions.map((v) => (
                    <li key={v.id} className="rounded-md border p-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium">Version {v.version}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(v.publishedAt).toLocaleString()}
                            {v.createdByName ? ` · ${v.createdByName}` : ""}
                          </p>
                          {v.changeNote ? (
                            <p className="mt-1 text-muted-foreground">{v.changeNote}</p>
                          ) : null}
                        </div>
                      </div>
                      <FormActions className="mt-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => void handlePreviewVersion(v.version)}>
                          View
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => void handleRestore(v.version)}>
                          Restore to draft
                        </Button>
                      </FormActions>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={previewVersion !== null} onOpenChange={(open) => !open && setPreviewVersion(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Version {previewVersion}</DialogTitle>
            <DialogDescription>Read-only snapshot from version history.</DialogDescription>
          </DialogHeader>
          {previewHtml ? (
            <div
              className="legal-document prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: addHeadingIds(previewHtml) }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
